import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import RiderDashboard from "@/pages/RiderDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import HistoryPage from "@/pages/HistoryPage";
import EarningsPage from "@/pages/EarningsPage";
import PublicDriverProfile from "@/pages/PublicDriverProfile";
import PublicRiderProfile from "@/pages/PublicRiderProfile";
import DriverProfileEdit from "@/pages/DriverProfileEdit";
import Leaderboard from "@/pages/Leaderboard";
import Account from "@/pages/Account";
import Community from "@/pages/Community";
import RideTracking from "@/pages/RideTracking";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import CommunityNotifier from "@/components/CommunityNotifier";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60" data-testid="loading-screen">
        Loading RideGY…
      </div>
    </div>
  );
}

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace state={{ from: location }} />;
  if (!user.role && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (role && user.role !== role) return <Navigate to={`/${user.role}`} replace />;
  return children;
}

function AuthCallbackRoute() {
  // Handles either /auth/callback or fragment-based session_id on any URL
  return <AuthCallback />;
}

function HashCatcher() {
  // Handle session_id arriving on root URL fragment
  const loc = useLocation();
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=") && loc.pathname !== "/auth/callback") {
    return <AuthCallback />;
  }
  return null;
}

function AppRoutes() {
  return (
    <>
      <ImpersonationBanner />
      <CommunityNotifier />
      <HashCatcher />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth/callback" element={<AuthCallbackRoute />} />
        <Route
          path="/onboarding"
          element={
            <Protected>
              <Onboarding />
            </Protected>
          }
        />
        <Route
          path="/rider"
          element={
            <Protected role="rider">
              <RiderDashboard />
            </Protected>
          }
        />
        <Route
          path="/rider/history"
          element={
            <Protected role="rider">
              <HistoryPage />
            </Protected>
          }
        />
        <Route
          path="/driver"
          element={
            <Protected role="driver">
              <DriverDashboard />
            </Protected>
          }
        />
        <Route
          path="/driver/earnings"
          element={
            <Protected role="driver">
              <EarningsPage />
            </Protected>
          }
        />
        <Route
          path="/driver/profile"
          element={
            <Protected role="driver">
              <DriverProfileEdit />
            </Protected>
          }
        />
        <Route
          path="/driver/history"
          element={
            <Protected role="driver">
              <HistoryPage />
            </Protected>
          }
        />
        <Route path="/d/:driverId" element={<PublicDriverProfile />} />
        <Route path="/r/:rider_id" element={<PublicRiderProfile />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/community" element={<Community />} />
        <Route
          path="/ride/:rideId"
          element={
            <Protected>
              <RideTracking />
            </Protected>
          }
        />
        <Route
          path="/account"
          element={
            <Protected>
              <Account />
            </Protected>
          }
        />
        <Route
          path="/admin"
          element={
            <Protected role="admin">
              <AdminDashboard />
            </Protected>
          }
        />
        <Route
          path="/admin/:tab"
          element={
            <Protected role="admin">
              <AdminDashboard />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-center"
          richColors
          toastOptions={{
            className: "rounded-none border-2 border-foreground font-medium",
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
